import React, { useState } from "react";
import { toast } from "sonner";
import api, { TIPO_OPERACION_OPTIONS, VALOR_OPERACION_OPTIONS, CREDITARIA_URL } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CheckCircle2 } from "lucide-react";

const INITIAL = {
  nombre: "",
  email: "",
  telefono: "",
  tipo_operacion: "",
  valor_operacion: "",
};

const REDIRECT_DELAY_MS = 1500;

const inputClass =
  "w-full px-4 py-3.5 text-sm text-white placeholder-white/25 border rounded-sm outline-none transition-colors duration-300 focus:border-white/30";
const inputStyle = { background: "#333333", borderColor: "rgba(255,255,255,0.08)", borderRadius: "2px" };
const selectClass =
  "w-full px-4 py-3.5 text-sm text-white border rounded-sm outline-none appearance-none cursor-pointer transition-colors duration-300 focus:border-white/30";

function validateForm(form) {
  const e = {};
  if (!form.nombre || form.nombre.trim().length < 2) e.nombre = "Escribe tu nombre completo";
  if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Correo inválido";
  const digits = form.telefono.replace(/\D/g, "");
  if (digits.length < 10) e.telefono = "Ingresa un teléfono válido (10 dígitos)";
  if (!form.tipo_operacion) e.tipo_operacion = "Selecciona una opción";
  if (!form.valor_operacion) e.valor_operacion = "Selecciona un rango";
  return e;
}

function SelectArrow() {
  return (
    <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M3 4.5L6 7.5L9 4.5" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export default function LeadForm({ open, onOpenChange }) {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        tipo_operacion: form.tipo_operacion,
        valor_operacion: form.valor_operacion,
      };
      const { data } = await api.post("/leads", payload);
      setDone(true);
      toast.success("Registro enviado. Redirigiendo a tu precalificación...");
      setTimeout(() => {
        window.location.href = data.redirect_url || CREDITARIA_URL;
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo enviar. Intenta de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(INITIAL);
    setErrors({});
    setDone(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent
        className="sm:max-w-[540px] border"
        style={{ background: "#1F1F1F", borderColor: "rgba(255,255,255,0.08)", borderRadius: "2px" }}
        data-testid="lead-form-dialog"
      >
        <DialogHeader>
          <div className="bf-teal-line mb-5" />
          <DialogTitle className="font-serif text-2xl md:text-3xl tracking-tight text-white">
            Inicia tu diagnóstico
          </DialogTitle>
          <DialogDescription className="text-xs tracking-wider uppercase mt-2" style={{ color: "rgba(255,255,255,0.3)" }}>
            Completa tus datos. Un asesor te contactará en las próximas 24 horas.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="lead-form-success">
            <CheckCircle2 className="h-10 w-10" style={{ color: "var(--teal)" }} />
            <p className="mt-5 font-serif text-xl text-white">Registro recibido</p>
            <p className="mt-2 text-xs uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.3)" }}>Redirigiendo a la precalificación...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(255,255,255,0.35)" }}>Nombre completo</label>
              <input
                data-testid="lead-form-nombre"
                value={form.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                placeholder="Nombre y apellidos"
                autoComplete="name"
                className={inputClass}
                style={inputStyle}
              />
              {errors.nombre && <p className="mt-1.5 text-xs" style={{ color: "var(--coral)" }}>{errors.nombre}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(255,255,255,0.35)" }}>Correo</label>
                <input
                  data-testid="lead-form-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="correo@ejemplo.com"
                  type="email"
                  autoComplete="email"
                  className={inputClass}
                  style={inputStyle}
                />
                {errors.email && <p className="mt-1.5 text-xs" style={{ color: "var(--coral)" }}>{errors.email}</p>}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(255,255,255,0.35)" }}>Teléfono / WhatsApp</label>
                <input
                  data-testid="lead-form-telefono"
                  value={form.telefono}
                  onChange={(e) => set("telefono", e.target.value)}
                  placeholder="33 1234 5678"
                  inputMode="tel"
                  autoComplete="tel"
                  className={inputClass}
                  style={inputStyle}
                />
                {errors.telefono && <p className="mt-1.5 text-xs" style={{ color: "var(--coral)" }}>{errors.telefono}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(255,255,255,0.35)" }}>Tipo de operación</label>
              <div className="relative">
                <select
                  data-testid="lead-form-tipo-operacion"
                  value={form.tipo_operacion}
                  onChange={(e) => set("tipo_operacion", e.target.value)}
                  className={selectClass}
                  style={{ ...inputStyle, color: form.tipo_operacion ? "#fff" : "rgba(255,255,255,0.25)" }}
                >
                  <option value="" disabled>Selecciona</option>
                  {TIPO_OPERACION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} style={{ background: "#333", color: "#fff" }}>{o.label}</option>
                  ))}
                </select>
                <SelectArrow />
              </div>
              {errors.tipo_operacion && <p className="mt-1.5 text-xs" style={{ color: "var(--coral)" }}>{errors.tipo_operacion}</p>}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(255,255,255,0.35)" }}>Valor aproximado de la operación</label>
              <div className="relative">
                <select
                  data-testid="lead-form-valor-operacion"
                  value={form.valor_operacion}
                  onChange={(e) => set("valor_operacion", e.target.value)}
                  className={selectClass}
                  style={{ ...inputStyle, color: form.valor_operacion ? "#fff" : "rgba(255,255,255,0.25)" }}
                >
                  <option value="" disabled>Selecciona un rango</option>
                  {VALOR_OPERACION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} style={{ background: "#333", color: "#fff" }}>{o.label}</option>
                  ))}
                </select>
                <SelectArrow />
              </div>
              {errors.valor_operacion && <p className="mt-1.5 text-xs" style={{ color: "var(--coral)" }}>{errors.valor_operacion}</p>}
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-form-submit"
              className="bf-btn-coral w-full px-6 py-4 mt-2 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</> : "Iniciar conversación"}
            </button>
            <p className="text-[10px] text-center tracking-wider uppercase" style={{ color: "rgba(255,255,255,0.18)" }}>
              Al continuar aceptas ser contactado por Brillo Finance. Información confidencial.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
